import { FC, useState } from "react";
import styles from "./styles.module.css";

import clsx from "clsx";
import { Upload, message, Space } from "antd";
import {
  UploadRequestError,
  UploadRequestOption,
} from "rc-upload/lib/interface";
import { UploadFile } from "antd/lib/upload/interface";
import { uploadImageRequest } from "api/upload";
import { getImageUrl } from "utils/urlParser";

import { Button } from "components/Button";
import { UploadIcon } from "assets/svgs";

interface ImageUploadProps {
  withContent?: boolean;
  desc?: string;
  onChange?: (fileName: string) => void;
  className?: string;
  initFileName?: string;
  aspect?: number;
}

function beforeUpload(file: any) {
  const isJpgOrPng =
    file.type === "image/jpeg" ||
    file.type === "image/png" ||
    file.type === "image/gif";
  const isLt5M = file.size / 1024 / 1024 < 5;
  return isJpgOrPng && isLt5M;
}

export const ImageUploadWithCrop: FC<ImageUploadProps> = ({
  desc = "5 MB — JPG, PNG, GIF 16:9 aspect ratio",
  initFileName,
  onChange,
  withContent = true,
  children,
  className,
}) => {
  const [image, setImage] = useState<UploadFile[]>(
    initFileName
      ? [
          {
            uid: "1",
            name: "initPreview",
            status: "done",
            url: getImageUrl(initFileName),
          },
        ]
      : []
  );

  const handleUploadChange = ({ fileList }: any) => {
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      const isJpgOrPng =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/gif";
      if (!isJpgOrPng) {
        message.error("You can only upload JPG/PNG/GIF file!");
        return;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("Image must smaller than 5MB!");
        return;
      }
    }
    setImage(fileList);
  };

  const onPreview = async (file: any) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const customRequest = async (option: UploadRequestOption) => {
    const { onSuccess, onError, file } = option;
    uploadImageRequest(file)
      .then((respones) => {
        onSuccess && onSuccess(respones.data, respones.request);
        const { data } = respones;
        onChange && onChange(data?.filename);
      })
      .catch((err: UploadRequestError) => {
        onError && onError(err);
      });
  };

  const handleRemove = () => {
    onChange && onChange("");
  };

  return (
    <div
      className={clsx(
        styles.root,
        image.length === 0 && styles.hideImagePreview,
        className
      )}
    >
      <Upload.Dragger
        listType="picture-card"
        fileList={image}
        onChange={handleUploadChange}
        maxCount={1}
        onPreview={onPreview}
        customRequest={customRequest}
        onRemove={handleRemove}
        className={clsx(image.length > 0 && styles.hideUploadBox, className)}
        beforeUpload={beforeUpload}
        accept="image/*"
      >
        {withContent && (
          <div className={styles.uploadContent}>
            <p className="text-12  center">{desc}</p>

            <Button size="large">
              <Space size={8}>
                <UploadIcon />
                <span>Upload</span>
              </Space>
            </Button>
            <p>or drag and drop</p>
          </div>
        )}

        {children}
      </Upload.Dragger>
    </div>
  );
};
